import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { 
  FiPieChart, FiTrendingUp, FiAlertCircle, FiClock, 
  FiBriefcase, FiDollarSign, FiArrowRight, FiInfo, FiTag 
} from "react-icons/fi";
import { Oval } from "react-loader-spinner";

const apiUrl = process.env.REACT_APP_API_URL;

function FinanceSummary({ filterMonth, filterYear }) {
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [accountSummary, setAccountSummary] = useState([]);
  const [profitsData, setProfitsData] = useState({ totalSoldPrice: 0, totalCost: 0, profit: 0 });
  const [unreceived, setUnreceived] = useState({ totalUnreceivedCost: 0, totalUnreceivedSold: 0 });
  const [unpaid, setUnpaid] = useState({ totalUnpaidCost: 0, totalUnpaidSold: 0 });
  const [remaining, setRemaining] = useState({ totalRemainingCost: 0, totalRemainingSold: 0 });
  const [grandTotal, setGrandTotal] = useState(0);
  const [monthlyBankTotal, setMonthlyBankTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const monthsAbbr = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const selectedMonthAbbr = useMemo(() => {
    return monthsAbbr[parseInt(filterMonth) - 1] || monthsAbbr[new Date().getMonth()];
  }, [filterMonth]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const queryParams = `?year=${filterYear}`;
      
      // 1. Basic Finance Summary
      const financeRes = await axios.get(`${apiUrl}/itemPrice/${selectedMonthAbbr}${queryParams}`);
      setData(financeRes.data);

      // 2. Account summary
      const accountsRes = await axios.get(`${apiUrl}/accounts/summary?month=${selectedMonthAbbr}&year=${filterYear}`);
      setAccountSummary(accountsRes.data.accounts || []);
      setGrandTotal(accountsRes.data.grandTotal || 0);
      setMonthlyBankTotal(accountsRes.data.monthlyTotal || 0);

      // 3. Detailed Financials
      const [pRes, uRes, upRes, rRes] = await Promise.all([
        Promise.all(["phone", "other", "screen", "ethio"].map(type => 
          axios.get(`${apiUrl}/finance/profitAll/${type}?month=${selectedMonthAbbr}&year=${filterYear}`)
        )),
        axios.get(`${apiUrl}/finance/unreceivedAll/${selectedMonthAbbr}${queryParams}`),
        axios.get(`${apiUrl}/finance/unPaidAll/${selectedMonthAbbr}${queryParams}`),
        axios.get(`${apiUrl}/finance/remainingAll/${selectedMonthAbbr}${queryParams}`)
      ]);

      // Combine profits
      const combinedProfits = pRes.reduce((acc, curr) => {
        const d = curr.data || {};
        return {
          totalSoldPrice: acc.totalSoldPrice + (Number(d.totalSoldPrice) || 0),
          totalCost: acc.totalCost + (Number(d.totalCost) || 0),
          profit: acc.profit + (Number(d.profit) || 0),
          details: { ...acc.details, [d.type || 'unknown']: Number(d.profit) || 0 }
        };
      }, { totalSoldPrice: 0, totalCost: 0, profit: 0, details: {} });


      setProfitsData(combinedProfits);
      setUnreceived(uRes.data);
      setUnpaid(upRes.data);
      setRemaining(rRes.data);

      // 4. Expenses
      const expensesRes = await axios.get(`${apiUrl}/api/expenses`);
      setExpenses(expensesRes.data || []);

      setLoading(false);
    } catch (err) {
      console.error("❌ Finance Fetch Error:", err);
      setError(err.response?.data?.message || "Failed to sync finance data");
      setLoading(false);
    }
  }, [selectedMonthAbbr, filterYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totals = useMemo(() => {
    const monthlyExpensesData = expenses.filter(exp => {
      const expDate = new Date(exp.date);
      return (expDate.getMonth() + 1) === parseInt(filterMonth) && expDate.getFullYear() === parseInt(filterYear);
    });

    const totalExpenses = monthlyExpensesData.reduce((acc, exp) => acc + (parseFloat(exp.amount) || 0), 0);
    
    // Monthly Revenue = Sold items price + Unpaid items price
    const revenue = (Number(profitsData.totalSoldPrice) || 0) + (Number(unpaid.totalUnpaidSold) || 0);
    // COGS = Cost of sold items + Cost of unpaid items
    const cogs = (Number(profitsData.totalCost) || 0) + (Number(unpaid.totalUnpaidCost) || 0);
    
    const grossProfit = revenue - cogs;
    const netProfit = grossProfit - totalExpenses;
    
    // Inventory Asset Value = Cost of remaining items + Cost of unreceived items
    const inventoryValue = (Number(remaining.totalRemainingCost) || 0) + (Number(unreceived.totalUnreceivedCost) || 0);
    
    return { 
      revenue: revenue || 0, 
      cogs: cogs || 0, 
      grossProfit: grossProfit || 0, 
      netProfit: netProfit || 0, 
      totalExpenses: totalExpenses || 0, 
      inventoryValue: inventoryValue || 0, 
      monthlyExpensesData 
    };

  }, [profitsData, unreceived, unpaid, remaining, expenses, filterMonth, filterYear]);

  if (loading && !data) return (
    <div className="flex justify-center items-center py-20">
      <Oval height={40} width={40} color="#10b981" secondaryColor="#34d399" strokeWidth={3} />
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {/* 1. Operational & Assets */}
      <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <FiPieChart className="w-24 h-24" />
        </div>
        
        <div className="flex items-center space-x-2 mb-6">
          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
            <FiBriefcase className="w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Assets & Operations</h2>
        </div>

        <div className="space-y-6">
          <div className="p-6 bg-slate-50 rounded-3xl">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Inventory Value (Assets)</p>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-black text-indigo-600">{totals.inventoryValue.toLocaleString()}</span>
              <span className="text-sm font-bold text-slate-400">ETB</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 flex items-center italic">
              <FiInfo className="mr-1" /> Estimated cost of unsold & incoming stock
            </p>
          </div>

          <div className="space-y-3 pt-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Inventory USD</span>
              <span className="font-bold text-slate-700">${data?.totalPrice?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Unreceived Price</span>
              <span className="font-bold text-slate-700">${data?.sendMoney?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Carry Over</span>
              <span className="font-bold text-slate-700">${data?.carryOver?.toLocaleString()}</span>
            </div>
            
            <div className={`flex justify-between items-center p-3 rounded-2xl mt-4 ${data?.status === "debt" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
              <span className="font-bold">Balance Status</span>
              <span className="font-black">
                {data?.difference?.toLocaleString()} {data?.status === "debt" ? "DEBT" : "REMAINING"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Profit & Performance (Sales logic) */}
      <div className="bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl shadow-indigo-900/20 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <FiTrendingUp className="w-24 h-24" />
        </div>

        <div className="flex items-center space-x-2 mb-6 text-green-400">
          <FiDollarSign className="w-5 h-5" />
          <h2 className="text-xl font-bold text-white">Monthly Performance</h2>
        </div>

        <div className="space-y-6">
          <div className="p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-md">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Net Monthly Profit</p>
                <div className="flex items-baseline space-x-2 text-green-400">
                  <span className="text-4xl font-black">{totals.netProfit.toLocaleString()}</span>
                  <span className="text-sm font-bold opacity-60">ETB</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Expenses</p>
                <p className="text-red-400 font-bold">-{totals.totalExpenses.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex justify-between items-center group">
              <span className="text-slate-400 text-sm flex items-center group-hover:text-white transition-colors"><FiArrowRight className="mr-2 w-3 h-3 text-green-500/50" /> Gross Profit (Margin)</span>
              <span className="font-bold text-slate-200">{totals.grossProfit.toLocaleString()} Br</span>
            </div>
            <div className="flex justify-between items-center group">
              <span className="text-slate-400 text-sm flex items-center group-hover:text-white transition-colors"><FiArrowRight className="mr-2 w-3 h-3 text-red-500/50" /> Cost of Goods (COGS)</span>
              <span className="font-bold text-slate-400">-{totals.cogs.toLocaleString()} Br</span>
            </div>
            <div className="flex justify-between items-center pt-2 group">
              <span className="text-slate-400 text-sm flex items-center group-hover:text-white transition-colors"><FiTag className="mr-2 w-3 h-3 text-indigo-400" /> Phones Profit</span>
              <span className="font-bold text-slate-200">{profitsData.details?.phone?.toLocaleString()} Br</span>
            </div>
          </div>

          <div className="pt-6 border-t border-white/10">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Monthly Sales (Revenue)</p>
                <p className="text-2xl font-black text-white">{totals.revenue.toLocaleString()} <span className="text-xs font-normal opacity-50">ETB</span></p>
              </div>
              <div className="text-right space-y-2">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Monthly Deposits</p>
                  <p className="text-sm font-bold text-green-400">+{monthlyBankTotal.toLocaleString()} <span className="text-[10px] opacity-50">Br</span></p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Current Balance</p>
                  <p className="text-sm font-bold text-indigo-400">{grandTotal.toLocaleString()} <span className="text-[10px] opacity-50">Br</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Receivables & Expenses */}
      <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col h-full">
        <div className="flex items-center space-x-2 mb-6">
          <div className="p-2 bg-orange-50 rounded-lg text-orange-600">
            <FiClock className="w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Monthly Receivables</h2>
        </div>

        <div className="space-y-4 flex-grow">
          <div className="p-4 bg-slate-50 rounded-3xl space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-slate-500">Unpaid Sales (Credit)</span>
              <span className="font-black text-orange-600">{unpaid.totalUnpaidSold?.toLocaleString()} Br</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-slate-500">Unreceived Stock Value</span>
              <span className="font-black text-indigo-500">{unreceived.totalUnreceivedCost?.toLocaleString()} Br</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-200">
              <span className="text-sm font-bold text-slate-500">Total Credit + Unreceived</span>
              <span className="font-black text-slate-900">{(unpaid.totalUnpaidSold + unreceived.totalUnreceivedCost).toLocaleString()} Br</span>
            </div>
          </div>

          <div className="pt-4">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 bg-red-50 rounded-lg text-red-600">
                <FiAlertCircle className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Recorded Expenses</h3>
            </div>
            <div className="space-y-2 text-sm text-slate-500 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {totals.monthlyExpensesData.length > 0 ? (
                totals.monthlyExpensesData.map(e => (
                  <div key={e.id} className="flex justify-between p-2 hover:bg-slate-50 rounded-xl">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-800">{e.description}</span>
                      <span className="text-[10px] text-slate-400">{e.date}</span>
                    </div>
                    <span className="font-bold text-red-500">-{parseFloat(e.amount).toLocaleString()}</span>
                  </div>
                ))
              ) : (
                <p className="text-center py-4 text-slate-400 italic">No expenses recorded for this month.</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-bold uppercase tracking-widest">
          <span className="flex items-center"><FiInfo className="mr-1" /> Sync: {selectedMonthAbbr} {filterYear}</span>
        </div>
      </div>
    </div>
  );
}

export default FinanceSummary;
